// A customer pickup signed by the customer (Shipping hub, Phases 2-4, 2026-10-28; behind GET /features
// shipping_signing.pickup). The hub's "Customer is here: start pickup" and the operator package guide's Confirm
// Pickup go through here instead of palletActions.confirmPickupAll / confirmPickup:
//   1. Load their vehicle   the load-out checklist (features/pallets/planPalletFlows.loadOutChecklist, step pickup;
//                           "They're taking only some": requireAll false), the lines sent WITH the request
//   2. Customer signs       POST /packaging/plans/{plan_id}/sign_requests {kind: 'pickup', pin (the Working-as
//                           session), pallet_ids, load_checklist, client_request_id} -> the QR panel
//                           (features/signing/SignRequestPanel): the pallets stay Staged until the customer signs
//   3. Hand over paperwork  "Print signed manifest for them"
// Paper fallback (POST .../pickup-paper) marks them picked up now; Cancel voids the code. Every step's who is the
// Working-as session (ui.identity); the API stores the operator who started the request (the customer may sign after
// that 10-minute session ended).
import { createSignRequest, newRequestId, pickupOnPaper } from '../signing/signRequestsApi';
import { andList } from '../signing/signWords';

const noop = () => {};

async function loadOut(args) {
  const { loadOutChecklist } = await import('../pallets/planPalletFlows');
  return loadOutChecklist(args);
}

async function openPanel(props) {
  const { signRequestPrompt } = await import('../signing/SignRequestPanel');
  return signRequestPrompt(props);
}

const nameOf = (plan, palletId) => {
  const p = ((plan && plan.pallets) || []).find((x) => Number(x.pallet_id) === Number(palletId));
  if (!p) return `P#${palletId}`;
  return p.short_id || (p.pallet_num != null ? `P${p.pallet_num}` : `P#${palletId}`);
};

/** The Staged pallets of a plan (or only `palletIds` among them). */
export function stagedPalletIds(plan, palletIds = null) {
  const keep = palletIds ? new Set(palletIds.map(Number)) : null;
  return ((plan && plan.pallets) || [])
    .filter((p) => p.status === 'Staged' && (!keep || keep.has(Number(p.pallet_id))))
    .map((p) => Number(p.pallet_id));
}

/**
 * Start a signed pickup. plan: {plan_id, order_number, customer_name?, pallets: [{pallet_id, short_id, status}]};
 * palletIds: only these (a pallet's own Confirm Pickup), else every Staged pallet. ui: the Working-as ui
 * (identity, confirm, openPdfTab). Resolves {done, message?, outcome?}: done = picked up (signed or on paper).
 * deps: test seam {loadOut, openPanel}.
 */
export async function startSignedPickup({ apiBase, plan, palletIds = null, ui, setBusy = noop, deps = {} }) {
  if (!plan) return { done: false };
  const d = { loadOut, openPanel, ...deps };
  const order = plan.order_number || 'this order';
  const staged = stagedPalletIds(plan, palletIds);

  // 1. load their vehicle (no list: an older API, checklists not set up: every pallet asked for, no lines)
  setBusy(true);
  let lo;
  try {
    lo = await d.loadOut({
      planId: plan.plan_id, step: 'pickup', palletIds, requireAll: false, recordLabel: 'Loaded: customer signs next',
    });
  } finally {
    setBusy(false);
  }
  if (!lo || lo.cancelled) return { done: false };
  const items = Array.isArray(lo.items) ? lo.items : null;
  const ids = items ? [...new Set(items.map((it) => Number(it.pallet_id)))] : staged;
  if (!ids.length) return { done: false, message: `${order}: no pallet was checked off as loaded. Nothing changed.` };

  // 2. who is handing off: the Working-as session (a sign-in when no one is)
  const pin = typeof ui.identity === 'function'
    ? await ui.identity('pickup')
    : await ui.pin(`Customer is here: enter your PIN to start the pickup of ${ids.length} pallet(s) for ${order}.`);
  if (!pin) return { done: false };

  setBusy(true);
  const created = await createSignRequest(apiBase, plan.plan_id, {
    kind: 'pickup', pin, palletIds: ids, loadChecklist: items, clientRequestId: newRequestId(),
  });
  setBusy(false);
  if (!created.ok || !created.body) {
    return { done: false, message: `${order}: couldn't start the signing: ${created.message}` };
  }

  const names = ids.map((id) => nameOf(plan, id));
  const onPaper = async () => {
    const p = await (typeof ui.identity === 'function' ? ui.identity('pickup') : Promise.resolve(pin));
    if (!p) return { ok: false, message: 'Not done: sign in first.' };
    const r = await pickupOnPaper(apiBase, plan.plan_id, { pin: p, palletIds: ids, loadChecklist: items });
    if (!r.ok) return { ok: false, message: `Not marked picked up: ${r.message}` };
    const b = r.body || {};
    if (b.signature_method === 'esign') {
      // the customer signed on the phone meanwhile: the API filed nothing on paper (already: true), so no manifest
      // to print and no "paper signature to file"
      const who = b.signed && b.signed.name;
      return {
        ok: true, esign: true, signed: b.signed || null,
        message: b.note || (who ? `These pallets were already signed for by ${who}.` : 'These pallets were already signed for.'),
      };
    }
    const url = b.manifest_url;
    if (url && ui.openPdfTab) {
      try { await ui.openPdfTab(`${apiBase}${url}`, `Pickup_Manifest_${order}.pdf`); } catch (_) { /* offered again */ }
    }
    // a phone opens no tab: the signing panel offers the manifest itself (a share-sheet button)
    const next = ui.openPdfTab ? 'Manifest opened to print.'
      : url ? 'Open the manifest below and print it for them to sign.' : 'No manifest came back: print one from a computer.';
    return { ok: true, message: `${andList(names)} marked picked up. ${next}`, documentUrl: url || null };
  };

  // 3. the customer signs (or paper, or cancel)
  const outcome = await d.openPanel({
    apiBase, ui, request: created.body, kind: 'pickup', orderNumber: plan.order_number,
    title: `Pickup · ${plan.customer_name || order}`,
    subtitle: `${order} · ${ids.length} pallet${ids.length === 1 ? '' : 's'}`,
    onPaper,
  });
  const st = outcome && outcome.status;
  if (st === 'signed') {
    if (outcome.already && outcome.message) return { done: true, outcome, message: `${order}: picked up. ${outcome.message}` };
    const who = outcome.signed && outcome.signed.name ? ` by ${outcome.signed.name}` : '';
    return { done: true, outcome, message: `${order}: picked up · signed${who}.` };
  }
  if (st === 'paper') return { done: true, outcome, message: `${order}: picked up · paper signature to file.` };
  if (st === 'void') return { done: false, outcome, message: `${order}: signing cancelled. The pallets are still ready for pickup.` };
  return { done: false, outcome, message: `${order}: the pallets stay ready for pickup until the customer signs.` };
}
